import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from "react-router-dom"; 
import { User, Briefcase, BookOpen, FileText, Settings, HelpCircle, Eye, EyeOff, ClipboardList, Coins, X, AlertCircle, Search, ArrowLeft, LogOut, Camera } from "lucide-react"; 
import api from "../services/api";
import { STORAGE_URL } from "../services/api";
import toast from "react-hot-toast";
import Header from "./Header"; 

export default function CandidateProfile() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("applications"); 
  const [loading, setLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  // Application State
  const [applications, setApplications] = useState([]);
  
  // Search aur Sort States
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("new-old");

  // Coin System States
  const [candidateCoins, setCandidateCoins] = useState(0);
  const [isCoinSystemActive, setIsCoinSystemActive] = useState(false);
  
  const [showInactiveModal, setShowInactiveModal] = useState(false);

  // QR System States for Recharge
  const [showQRModal, setShowQRModal] = useState(false);
  const [utrNumber, setUtrNumber] = useState('');
  const [submittingUtr, setSubmittingUtr] = useState(false);

  // Password visibility states
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [personalDetails, setPersonalDetails] = useState({
    fullName: '', email: '', mobileNumber: '', gender: '', dateOfBirth: '', currentCity: '', currentAddress: '', preferredJobLocation: '', careerObjective: ''
  });
  const [personalDetailsForm, setPersonalDetailsForm] = useState(personalDetails);
  const [isEditingPersonal, setIsEditingPersonal] = useState(false);

  // Profile Image State
  const [profileImage, setProfileImage] = useState(null);

  // Experience State
  const [experienceList, setExperienceList] = useState([]);
  const [experienceForm, setExperienceForm] = useState({ companyName: '', jobTitle: '', employmentType: '', startDate: '', endDate: '', jobDescription: '' });
  const [isAddingExperience, setIsAddingExperience] = useState(false);

  // Education State
  const [educationList, setEducationList] = useState([]);
  const [educationForm, setEducationForm] = useState({ qualification: '', collegeUniversity: '', specialization: '', percentageCgpa: '', passingYear: '' });
  const [isAddingEducation, setIsAddingEducation] = useState(false);

  const [resumeData, setResumeData] = useState(null);
  const [settingsData, setSettingsData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState(0);

  const faqItems = [
    { question: 'How to apply for jobs?', answer: 'Go to the jobs section, browse available openings, open the job details, and click the apply button to submit your profile.' },
    { question: 'How to update resume?', answer: 'Open the resume section, click upload or replace resume, select a valid PDF, DOC, or DOCX file from your device, and it will be updated locally.' },
    { question: 'How to change password?', answer: 'Open the settings section, enter your current password, set a new password, confirm it, and save the changes.' },
    { question: 'How to contact support?', answer: 'Use the Need Help button in the FAQ section to view support guidance and next-step help details.' }
  ];

  const getUpiId = () => {
    return (import.meta.env && import.meta.env.VITE_UPI_ID) 
           || (process.env && process.env.REACT_APP_UPI_ID) 
           || "kadte74110630@barodampay";
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("candidateToken");
        if (!token) {
          setIsInitializing(false);
          return;
        }
        const headers = { Authorization: `Bearer ${token}` };

        try {
            const settingsRes = await api.get("/settings");
            if(settingsRes.data) {
                setIsCoinSystemActive(settingsRes.data.candidate_coin_system == 1);
            }
        } catch (setErr) {
            console.error("Failed to fetch settings", setErr);
        }

        try {
          const appRes = await api.get("/candidate/applications", { headers });
          if(appRes.data) {
            setApplications(appRes.data.data || appRes.data); 
          }
        } catch (appErr) {
          console.warn("Applications fetch error:", appErr);
        }

        const profileRes = await api.get("/candidate/profile", { headers });
        
        if (profileRes.data) {
          const profileData = profileRes.data.data || profileRes.data.candidate || profileRes.data;

          const fetchedData = {
            fullName: profileData.name || '',
            email: profileData.email || '',
            mobileNumber: profileData.phone || '',
            gender: profileData.gender || '',
            dateOfBirth: profileData.dob || '',
            currentCity: profileData.current_city || '',
            currentAddress: profileData.current_address || '',
            preferredJobLocation: profileData.preferred_location || '',
            careerObjective: profileData.career_objective || ''
          };
          
          setPersonalDetails(fetchedData);
          setPersonalDetailsForm(fetchedData);
          
          if (profileData.profile_photo_url) {
            let photoUrl = profileData.profile_photo_url;
            photoUrl = photoUrl.replace('/api/', '/').replace('api/', '');
            if (!photoUrl.startsWith('http')) {
              photoUrl = `http://127.0.0.1:8000/storage/${photoUrl.replace(/^\/?storage\//, '')}`;
            }
            setProfileImage(photoUrl);
          }
          
          const currentCoins = profileData.coins ? parseInt(profileData.coins) : 0;
          setCandidateCoins(currentCoins);

          if (currentCoins <= 0 || profileData.status === 'inactive') {
              setShowInactiveModal(true);
          }

          const savedLocal = JSON.parse(localStorage.getItem('candidate') || '{}');
          localStorage.setItem('candidate', JSON.stringify({ ...savedLocal, ...profileData, coins: currentCoins }));

          if (profileData.resume_url) {
            setResumeData({
              name: profileData.resume_url.split('/').pop(),
              uploadDate: "Saved in Database",
              url: profileData.resume_url
            });
          }
        }

        const expRes = await api.get("/candidate/experiences", { headers });
        if(expRes.data) {
          const mappedExp = expRes.data.map(e => ({
            id: e.id,
            companyName: e.company_name,
            jobTitle: e.job_title,
            employmentType: e.employment_type,
            startDate: e.start_date,
            endDate: e.end_date,
            jobDescription: e.job_description
          }));
          setExperienceList(mappedExp);
        }

        const eduRes = await api.get("/candidate/educations", { headers });
        if(eduRes.data) {
          const mappedEdu = eduRes.data.map(ed => ({
            id: ed.id,
            qualification: ed.qualification,
            collegeUniversity: ed.college_university,
            specialization: ed.specialization,
            percentageCgpa: ed.percentage_cgpa,
            passingYear: ed.passing_year
          }));
          setEducationList(mappedEdu);
        }

      } catch (err) {
        console.error("Failed to fetch candidate data", err);
      } finally {
        setIsInitializing(false);
      }
    };
    fetchData();
  }, []);

  const handlePersonalInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'mobileNumber') {
      const numericValue = value.replace(/\D/g, '');
      setPersonalDetailsForm((prev) => ({ ...prev, [name]: numericValue }));
    } else {
      setPersonalDetailsForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleEditPersonal = () => {
    setPersonalDetailsForm(personalDetails);
    setIsEditingPersonal(true);
  };

  const handleSavePersonal = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("candidateToken");
      const payload = {
        name: personalDetailsForm.fullName,
        email: personalDetailsForm.email,
        phone: personalDetailsForm.mobileNumber,
        gender: personalDetailsForm.gender,
        dob: personalDetailsForm.dateOfBirth,
        current_city: personalDetailsForm.currentCity,
        current_address: personalDetailsForm.currentAddress,
        preferred_location: personalDetailsForm.preferredJobLocation,
        career_objective: personalDetailsForm.careerObjective
      };

      await api.put("/candidate/profile", payload, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      setPersonalDetails(personalDetailsForm);
      setIsEditingPersonal(false);
      toast.success("Profile updated successfully!");
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to update profile.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelPersonal = () => {
    setPersonalDetailsForm(personalDetails);
    setIsEditingPersonal(false);
  };

  const handleProfilePhotoUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("profile_photo", file);

    try {
      const token = localStorage.getItem("candidateToken");
      const res = await api.post("/candidate/profile-photo-upload", formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
          'Accept': 'application/json'
        }
      });

      let newPhotoUrl = res.data.profile_photo_url;
      newPhotoUrl = newPhotoUrl.replace('/api/', '/').replace('api/', '');
      if (!newPhotoUrl.startsWith('http')) {
        newPhotoUrl = `http://127.0.0.1:8000/storage/${newPhotoUrl.replace(/^\/?storage\//, '')}`;
      }

      setProfileImage(newPhotoUrl);
      toast.success("Profile photo uploaded successfully!");
    } catch (error) {
      console.error("Photo upload error:", error.response || error);
      toast.error(error.response?.data?.message || "Failed to upload profile photo.");
    }
  };

  const handleSaveExperience = async () => {
    try {
      const token = localStorage.getItem("candidateToken");
      const payload = {
        company_name: experienceForm.companyName,
        job_title: experienceForm.jobTitle,
        employment_type: experienceForm.employmentType,
        start_date: experienceForm.startDate,
        end_date: experienceForm.endDate,
        job_description: experienceForm.jobDescription
      };

      const res = await api.post("/candidate/experiences", payload, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      const savedExp = res.data.data;
      setExperienceList(prev => [...prev, {
        id: savedExp.id,
        companyName: savedExp.company_name,
        jobTitle: savedExp.job_title,
        employmentType: savedExp.employment_type,
        startDate: savedExp.start_date,
        endDate: savedExp.end_date,
        jobDescription: savedExp.job_description
      }]);

      setExperienceForm({ companyName: '', jobTitle: '', employmentType: '', startDate: '', endDate: '', jobDescription: '' });
      setIsAddingExperience(false);
      toast.success("Experience added successfully!");
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to save experience.");
    }
  };

  const handleDeleteExperience = async (id, index) => {
    try {
      const token = localStorage.getItem("candidateToken");
      await api.delete(`/candidate/experiences/${id}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      setExperienceList(experienceList.filter((_, i) => i !== index));
      toast.success("Experience deleted successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete experience.");
    }
  };

  const handleSaveEducation = async () => {
    try {
      const token = localStorage.getItem("candidateToken");
      const payload = {
        qualification: educationForm.qualification,
        college_university: educationForm.collegeUniversity,
        specialization: educationForm.specialization,
        percentage_cgpa: educationForm.percentageCgpa,
        passing_year: educationForm.passingYear
      };

      const res = await api.post("/candidate/educations", payload, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      const savedEdu = res.data.data;
      setEducationList(prev => [...prev, {
        id: savedEdu.id,
        qualification: savedEdu.qualification,
        collegeUniversity: savedEdu.college_university,
        specialization: savedEdu.specialization,
        percentageCgpa: savedEdu.percentage_cgpa,
        passingYear: savedEdu.passing_year
      }]);

      setEducationForm({ qualification: '', collegeUniversity: '', specialization: '', percentageCgpa: '', passingYear: '' });
      setIsAddingEducation(false);
      toast.success("Education added successfully!");
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to save education.");
    }
  };

  const handleDeleteEducation = async (id, index) => {
    try {
      const token = localStorage.getItem("candidateToken");
      await api.delete(`/candidate/educations/${id}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      setEducationList(educationList.filter((_, i) => i !== index));
      toast.success("Education deleted successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete education.");
    }
  };

  const handleResumeUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("resume", file);

    try {
      const token = localStorage.getItem("candidateToken");
      const res = await api.post("/candidate/resume-upload", formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
          'Accept': 'application/json'
        }
      });

      setResumeData({ 
        file, 
        name: file.name, 
        uploadDate: new Date().toLocaleDateString(), 
        url: res.data.resume_url 
      });
      toast.success("Resume uploaded successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Failed to upload resume.");
    }
  };

  const handleDownloadResume = () => {
    if (!resumeData || !resumeData.url) return;
    const link = document.createElement('a');
    link.href = resumeData.url;
    link.download = resumeData.name || "resume.pdf";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteResume = async () => {
    try {
      const token = localStorage.getItem("candidateToken");
      await api.delete("/candidate/resume-delete", {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      setResumeData(null);
      toast.success("Resume deleted successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete resume.");
    }
  };

  const handleSettingsInputChange = (e) => {
    const { name, value } = e.target;
    setSettingsData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSavePassword = async () => {
    if (!settingsData.currentPassword || !settingsData.newPassword) {
      toast.error('Please fill all password fields.');
      return;
    }
    if (settingsData.newPassword !== settingsData.confirmPassword) {
      toast.error('New passwords do not match.');
      return;
    }

    try {
      const token = localStorage.getItem("candidateToken");
      const payload = {
        current_password: settingsData.currentPassword,
        new_password: settingsData.newPassword
      };

      await api.put("/candidate/password-update", payload, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      toast.success("Password updated successfully!");
      setSettingsData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to update password.");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("candidate");
    localStorage.removeItem("candidateToken");
    toast.success("Logged out successfully");
    navigate("/");
  };

  const handleDeleteAccount = () => {
    setShowDeleteAccountModal(false);
    localStorage.removeItem("candidate");
    localStorage.removeItem("candidateToken");
    setTimeout(() => navigate("/"), 2000);
  };

  const calculateCompleteness = () => {
    let score = 0;
    const requiredPersonalFields = ['fullName', 'email', 'mobileNumber', 'gender', 'dateOfBirth', 'currentCity', 'currentAddress', 'preferredJobLocation'];
    const filledFields = requiredPersonalFields.filter(field => personalDetails[field] && personalDetails[field].toString().trim() !== '');
    score += Math.round((filledFields.length / requiredPersonalFields.length) * 40);

    if (experienceList.length > 0) score += 20;
    if (educationList.length > 0) score += 20;
    if (resumeData) score += 20;

    return Math.min(score, 100);
  };

  const profileCompleteness = calculateCompleteness();
  const renderValue = (value) => value ? value : '-';

  const filteredAndSortedApplications = [...applications]
    .filter(app => {
      const term = searchTerm.toLowerCase();
      const title = (app.job?.title || app.job_title || "").toLowerCase();
      const company = (app.job?.company_name || app.company_name || "").toLowerCase();
      return title.includes(term) || company.includes(term);
    })
    .sort((a, b) => {
      const dateA = new Date(a.applied_on || a.created_at);
      const dateB = new Date(b.applied_on || b.created_at);
      if (sortOrder === "new-old") {
        return dateB - dateA;
      } else {
        return dateA - dateB;
      }
    });

  if (isInitializing) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <div className="flex-grow flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-yellow-500"></div>
        </div>
      </div>
    );
  }

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', { 
      day: 'numeric', month: 'short', year: 'numeric', 
      hour: '2-digit', minute: '2-digit', hour12: true 
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col w-full relative">
      <Header />
      
      <div className="max-w-7xl mx-auto px-6 pt-6 pb-2 w-full">
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center text-sm font-bold text-gray-500 hover:text-yellow-600 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
        </button>
      </div>

      <main className="max-w-7xl mx-auto px-6 pt-2 pb-8 flex-grow w-full">
        
        {/* Top Profile Banner */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex flex-1 items-center gap-4">
            
            <div className="relative w-16 h-16 bg-yellow-500 text-black font-bold text-2xl rounded-2xl flex items-center justify-center shadow overflow-hidden shrink-0 group cursor-pointer">
              {profileImage ? (
                <img src={profileImage} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <span>{personalDetails.fullName ? personalDetails.fullName.charAt(0).toUpperCase() : "C"}</span>
              )}
              
              <label className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                <Camera className="text-white w-6 h-6" />
                <input 
                  type="file" 
                  accept="image/*" 
                  className="hidden" 
                  onChange={handleProfilePhotoUpload} 
                />
              </label>
            </div>

            <div>
              <h1 className="text-xl font-bold text-gray-900">{personalDetails.fullName || "Candidate Profile"}</h1>
              <p className="text-sm text-gray-500">{personalDetails.email}</p>
              <p className="text-sm text-gray-500">{personalDetails.mobileNumber || "Phone not added"} • {personalDetails.currentCity || "City not added"}</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-4 w-full md:w-auto">
            {isCoinSystemActive && (
              <div className="w-full sm:w-auto bg-gradient-to-br from-yellow-50 to-orange-50 p-4 rounded-xl border border-yellow-200 flex items-center justify-between gap-6 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center text-2xl shadow-inner border border-yellow-300 shrink-0">
                    🪙
                  </div>
                  <div className="pr-2">
                    <p className="text-[10px] text-yellow-700 font-extrabold uppercase tracking-wider">Wallet Balance</p>
                    <h3 className="text-xl font-black text-gray-900 leading-tight">
                      {candidateCoins} <span className="text-sm text-yellow-700 font-bold">Coins</span>
                    </h3>
                  </div>
                </div>
                <button 
                  onClick={() => setShowQRModal(true)} 
                  className="bg-yellow-500 hover:bg-black hover:text-yellow-500 text-black font-extrabold text-xs px-4 py-2 rounded-lg transition-colors shadow-sm shrink-0"
                >
                  Recharge
                </button>
              </div>
            )}

            <div className="w-full sm:w-64 bg-gray-50 p-4 rounded-xl border border-gray-100">
              <div className="flex justify-between text-sm mb-1">
                <span className="font-medium text-gray-600">Completeness</span>
                <span className="font-bold text-gray-900">{profileCompleteness}%</span>
              </div>
              <div className="w-full bg-gray-200 h-2.5 rounded-full overflow-hidden">
                <div className="bg-yellow-500 h-full rounded-full transition-all duration-500" style={{ width: `${profileCompleteness}%` }}></div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 flex flex-col h-full min-h-[500px]">
            <div className="space-y-1 flex-grow">
              <button onClick={() => setActiveTab("applications")} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${activeTab === 'applications' ? 'bg-yellow-500 text-black font-semibold shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`}>
                <ClipboardList size={18} /> Application Status
              </button>
              <button onClick={() => setActiveTab("personal")} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${activeTab === 'personal' ? 'bg-yellow-500 text-black font-semibold shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`}>
                <User size={18} /> Personal Details
              </button>
              <button onClick={() => setActiveTab("experience")} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${activeTab === 'experience' ? 'bg-yellow-500 text-black font-semibold shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`}>
                <Briefcase size={18} /> Work Experience
              </button>
              <button onClick={() => setActiveTab("education")} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${activeTab === 'education' ? 'bg-yellow-500 text-black font-semibold shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`}>
                <BookOpen size={18} /> Education
              </button>
              <button onClick={() => setActiveTab("resume")} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${activeTab === 'resume' ? 'bg-yellow-500 text-black font-semibold shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`}>
                <FileText size={18} /> Resume
              </button>
              <button onClick={() => setActiveTab("settings")} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${activeTab === 'settings' ? 'bg-yellow-500 text-black font-semibold shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`}>
                <Settings size={18} /> Settings
              </button>
              <button onClick={() => setActiveTab("faq")} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${activeTab === 'faq' ? 'bg-yellow-500 text-black font-semibold shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`}>
                <HelpCircle size={18} /> FAQ & Help
              </button>
            </div>
            
            <div className="mt-6 pt-4 border-t border-gray-100">
              <button 
                onClick={handleLogout} 
                className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl text-sm font-bold text-red-600 bg-red-50 hover:bg-red-100 transition-colors"
              >
                <LogOut size={18} /> Logout
              </button>
            </div>
          </div>

          <div className="md:col-span-3 bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
            
            {activeTab === 'applications' && (
              <div>
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 pb-4 border-b border-gray-100 gap-4">
                    <div className="flex items-center gap-3">
                        <h2 className="text-xl font-extrabold text-gray-900">My Job Applications</h2>
                        <span className="bg-yellow-100 text-yellow-800 text-xs font-bold px-3 py-1 rounded-full border border-yellow-200">Total: {applications.length}</span>
                    </div>

                    {applications.length > 0 && (
                        <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
                            <div className="relative w-full sm:w-56 text-gray-500">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input 
                                    type="text" 
                                    placeholder="Search jobs or company..." 
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-9 pr-3 py-2 text-sm font-semibold border-2 border-gray-100 bg-gray-50 focus:bg-white rounded-xl outline-none focus:border-yellow-500 transition-colors"
                                />
                            </div>
                            <select 
                                value={sortOrder} 
                                onChange={(e) => setSortOrder(e.target.value)}
                                className="w-full sm:w-auto px-3 py-2 text-sm font-semibold border-2 border-gray-100 bg-gray-50 focus:bg-white rounded-xl outline-none focus:border-yellow-500 transition-colors cursor-pointer"
                            >
                                <option value="new-old">Sort: Newest First</option>
                                <option value="old-new">Sort: Oldest First</option>
                            </select>
                        </div>
                    )}
                </div>
                
                {applications.length === 0 ? (
                  <div className="bg-gray-50 p-10 rounded-2xl text-center border border-gray-100 flex flex-col items-center justify-center">
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm mb-4">
                      <ClipboardList size={32} className="text-gray-300" />
                    </div>
                    <p className="text-gray-600 font-bold mb-1">No applications found</p>
                    <p className="text-gray-400 text-sm mb-6">You haven't applied for any jobs yet.</p>
                    <button onClick={() => navigate('/job-listing')} className="bg-yellow-500 text-black px-6 py-2.5 rounded-xl font-bold text-sm hover:bg-yellow-600 transition-colors shadow-sm">
                      Browse Jobs
                    </button>
                  </div>
                ) : filteredAndSortedApplications.length === 0 ? (
                  <div className="bg-gray-50 p-10 rounded-2xl text-center border border-gray-100 flex flex-col items-center justify-center">
                      <p className="text-gray-500 font-bold">No results found for "{searchTerm}"</p>
                  </div>
                ) : (
                  <div className="space-y-4 max-h-[550px] overflow-y-auto pr-2" style={{ scrollbarWidth: 'thin', scrollbarColor: '#FED547 #f3f4f6' }}>
                    {filteredAndSortedApplications.map((app, index) => {
                      const status = app.status ? app.status.toLowerCase() : 'applied';
                      
                      let badgeColor = "bg-blue-50 text-blue-600 border-blue-200";
                      if (status === 'shortlisted' || status === 'accepted') badgeColor = "bg-green-50 text-green-700 border-green-200";
                      else if (status === 'rejected') badgeColor = "bg-red-50 text-red-600 border-red-200";
                      else if (status === 'interview') badgeColor = "bg-yellow-100 text-yellow-800 border-yellow-300";

                      return (
                        <div key={`app-${index}-${app.id}`} className="p-5 border border-gray-100 rounded-xl flex flex-col sm:flex-row sm:items-center gap-4 bg-white hover:border-yellow-300 transition-all shadow-sm hover:shadow relative pl-14 mr-1">
                          
                          <div className="absolute left-4 top-1/2 -translate-y-1/2 w-7 h-7 bg-yellow-50 text-yellow-600 font-extrabold text-sm rounded-full flex items-center justify-center border border-yellow-200 shadow-sm">
                              {index + 1}
                          </div>

                          <div className="flex-1">
                            <h4 className="font-extrabold text-gray-900 text-lg">{app.job?.title || app.job_title || 'Job Title Unknown'}</h4>
                            <p className="text-sm font-bold text-gray-500 mt-1">{app.job?.company_name || app.company_name || 'Company Name Unknown'}</p>
                          </div>
                          
                          <div className="shrink-0 sm:px-6 sm:border-r sm:border-gray-100 text-left sm:text-center mt-2 sm:mt-0">
                            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-0.5">Applied on</p>
                            <p className="text-xs font-extrabold text-gray-700">{formatDateTime(app.applied_on || app.created_at)}</p>
                          </div>

                          <div className="shrink-0 text-left sm:text-right sm:w-32 mt-2 sm:mt-0">
                            <span className={`inline-block text-center px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider border shadow-sm ${badgeColor}`}>
                              {app.status || 'Applied'}
                            </span>
                          </div>

                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'personal' && (
              <div>
                <div className="flex justify-between items-center mb-6 pb-3 border-b border-gray-100">
                  <h2 className="text-xl font-extrabold text-gray-900">Personal Details</h2>
                  <div className="flex gap-2 items-center">
                    {/* 🌟 Personal Details tab ke andar profile photo upload button */}
                    {isEditingPersonal && (
                      <label className="bg-blue-50 text-blue-600 border border-blue-200 px-4 py-2 rounded-xl text-xs font-bold cursor-pointer hover:bg-blue-100 transition-colors shadow-sm">
                        Change Photo
                        <input type="file" accept="image/*" className="hidden" onChange={handleProfilePhotoUpload} />
                      </label>
                    )}

                    {!isEditingPersonal ? (
                      <button type="button" className="bg-yellow-500 text-black px-5 py-2 rounded-xl text-sm font-bold shadow-sm hover:bg-yellow-600" onClick={handleEditPersonal}>Edit Profile</button>
                    ) : (
                      <>
                        <button type="button" disabled={loading} className="bg-gray-900 text-yellow-500 px-5 py-2 rounded-xl text-sm font-bold shadow-sm hover:bg-black disabled:opacity-50" onClick={handleSavePersonal}>
                          {loading ? "Saving..." : "Save Changes"}
                        </button>
                        <button type="button" className="bg-gray-100 text-gray-600 px-5 py-2 rounded-xl text-sm font-bold hover:bg-gray-200" onClick={handleCancelPersonal}>Cancel</button>
                      </>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-8 text-sm">
                  <div>
                    <span className="text-gray-400 font-bold text-[11px] uppercase tracking-wider block mb-1.5">Full Name</span>
                    {isEditingPersonal ? <input type="text" name="fullName" value={personalDetailsForm.fullName} onChange={handlePersonalInputChange} className="w-full border-2 border-gray-200 p-2.5 rounded-xl outline-none focus:border-yellow-500 font-semibold text-gray-900" /> : <p className="font-bold text-gray-900 text-base">{renderValue(personalDetails.fullName)}</p>}
                  </div>
                  <div>
                    <span className="text-gray-400 font-bold text-[11px] uppercase tracking-wider block mb-1.5">
                      {personalDetails.email ? "Email (Read Only)" : "Email *"}
                    </span>
                    {isEditingPersonal && !personalDetails.email ? (
                      <input 
                        type="email" 
                        name="email" 
                        value={personalDetailsForm.email} 
                        onChange={handlePersonalInputChange} 
                        className="w-full border-2 border-gray-200 p-2.5 rounded-xl outline-none focus:border-yellow-500 font-semibold text-gray-900" 
                        placeholder="Enter your email"
                      />
                    ) : (
                      <p className="font-bold text-gray-900 text-base">{renderValue(personalDetails.email)}</p>
                    )}
                  </div>
                  <div>
                    <span className="text-gray-400 font-bold text-[11px] uppercase tracking-wider block mb-1.5">Mobile Number</span>
                    {isEditingPersonal ? <input type="text" name="mobileNumber" value={personalDetailsForm.mobileNumber} onChange={handlePersonalInputChange} className="w-full border-2 border-gray-200 p-2.5 rounded-xl outline-none focus:border-yellow-500 font-semibold text-gray-900" /> : <p className="font-bold text-gray-900 text-base">{renderValue(personalDetails.mobileNumber)}</p>}
                  </div>
                  <div>
                    <span className="text-gray-400 font-bold text-[11px] uppercase tracking-wider block mb-1.5">Gender</span>
                    {isEditingPersonal ? (
                      <select name="gender" value={personalDetailsForm.gender} onChange={handlePersonalInputChange} className="w-full border-2 border-gray-200 p-2.5 rounded-xl outline-none focus:border-yellow-500 font-semibold text-gray-900 bg-white">
                        <option value="">Select</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                    ) : <p className="font-bold text-gray-900 text-base">{renderValue(personalDetails.gender)}</p>}
                  </div>
                  <div>
                    <span className="text-gray-400 font-bold text-[11px] uppercase tracking-wider block mb-1.5">Date of Birth</span>
                    {isEditingPersonal ? <input type="date" name="dateOfBirth" value={personalDetailsForm.dateOfBirth} onChange={handlePersonalInputChange} className="w-full border-2 border-gray-200 p-2.5 rounded-xl outline-none focus:border-yellow-500 font-semibold text-gray-900" /> : <p className="font-bold text-gray-900 text-base">{renderValue(personalDetails.dateOfBirth)}</p>}
                  </div>
                  <div>
                    <span className="text-gray-400 font-bold text-[11px] uppercase tracking-wider block mb-1.5">Current City</span>
                    {isEditingPersonal ? <input type="text" name="currentCity" value={personalDetailsForm.currentCity} onChange={handlePersonalInputChange} className="w-full border-2 border-gray-200 p-2.5 rounded-xl outline-none focus:border-yellow-500 font-semibold text-gray-900" /> : <p className="font-bold text-gray-900 text-base">{renderValue(personalDetails.currentCity)}</p>}
                  </div>
                  <div className="md:col-span-2">
                    <span className="text-gray-400 font-bold text-[11px] uppercase tracking-wider block mb-1.5">Current Address</span>
                    {isEditingPersonal ? <textarea name="currentAddress" rows="2" value={personalDetailsForm.currentAddress} onChange={handlePersonalInputChange} className="w-full border-2 border-gray-200 p-2.5 rounded-xl outline-none focus:border-yellow-500 font-semibold text-gray-900 resize-none" /> : <p className="font-bold text-gray-900 text-base">{renderValue(personalDetails.currentAddress)}</p>}
                  </div>
                  <div>
                    <span className="text-gray-400 font-bold text-[11px] uppercase tracking-wider block mb-1.5">Preferred Job Location</span>
                    {isEditingPersonal ? <input type="text" name="preferredJobLocation" value={personalDetailsForm.preferredJobLocation} onChange={handlePersonalInputChange} className="w-full border-2 border-gray-200 p-2.5 rounded-xl outline-none focus:border-yellow-500 font-semibold text-gray-900" /> : <p className="font-bold text-gray-900 text-base">{renderValue(personalDetails.preferredJobLocation)}</p>}
                  </div>
                  <div className="md:col-span-2">
                    <span className="text-gray-400 font-bold text-[11px] uppercase tracking-wider block mb-1.5">Career Objective</span>
                    {isEditingPersonal ? <textarea name="careerObjective" rows="3" value={personalDetailsForm.careerObjective} onChange={handlePersonalInputChange} className="w-full border-2 border-gray-200 p-2.5 rounded-xl outline-none focus:border-yellow-500 font-semibold text-gray-900 resize-none" /> : <p className="font-bold text-gray-900 text-base">{renderValue(personalDetails.careerObjective)}</p>}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'experience' && (
              <div>
                <div className="flex justify-between items-center mb-6 pb-3 border-b border-gray-100">
                  <h2 className="text-xl font-extrabold text-gray-900">Work Experience</h2>
                  <button type="button" className="bg-yellow-500 text-black px-4 py-2 rounded-xl text-sm font-bold hover:bg-yellow-600 shadow-sm" onClick={() => setIsAddingExperience(true)}>+ Add Experience</button>
                </div>

                {isAddingExperience && (
                  <div className="bg-gray-50 p-6 rounded-2xl border border-gray-200 mb-6 space-y-4">
                    <h3 className="font-extrabold text-gray-900">Add New Experience</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <input type="text" placeholder="Company Name" value={experienceForm.companyName} onChange={(e) => setExperienceForm({...experienceForm, companyName: e.target.value})} className="border-2 border-gray-200 p-3 rounded-xl outline-none focus:border-yellow-500 font-semibold" />
                      <input type="text" placeholder="Job Title" value={experienceForm.jobTitle} onChange={(e) => setExperienceForm({...experienceForm, jobTitle: e.target.value})} className="border-2 border-gray-200 p-3 rounded-xl outline-none focus:border-yellow-500 font-semibold" />
                      <input type="text" placeholder="Employment Type (e.g. Full-time)" value={experienceForm.employmentType} onChange={(e) => setExperienceForm({...experienceForm, employmentType: e.target.value})} className="border-2 border-gray-200 p-3 rounded-xl outline-none focus:border-yellow-500 font-semibold" />
                      <div className="flex gap-3">
                        <div className="w-1/2">
                          <span className="text-[10px] text-gray-400 font-bold uppercase ml-1 block mb-1">Start Date</span>
                          <input type="date" value={experienceForm.startDate} onChange={(e) => setExperienceForm({...experienceForm, startDate: e.target.value})} className="border-2 w-full border-gray-200 p-2.5 rounded-xl outline-none focus:border-yellow-500 font-semibold text-gray-600" />
                        </div>
                        <div className="w-1/2">
                          <span className="text-[10px] text-gray-400 font-bold uppercase ml-1 block mb-1">End Date</span>
                          <input type="date" value={experienceForm.endDate} onChange={(e) => setExperienceForm({...experienceForm, endDate: e.target.value})} className="border-2 w-full border-gray-200 p-2.5 rounded-xl outline-none focus:border-yellow-500 font-semibold text-gray-600" />
                        </div>
                      </div>
                      <textarea placeholder="Job Description & Responsibilities" rows="3" value={experienceForm.jobDescription} onChange={(e) => setExperienceForm({...experienceForm, jobDescription: e.target.value})} className="border-2 border-gray-200 p-3 rounded-xl outline-none focus:border-yellow-500 font-semibold md:col-span-2 resize-none" />
                    </div>
                    <div className="flex gap-3 pt-2">
                      <button type="button" className="bg-gray-900 text-yellow-500 px-6 py-2.5 rounded-xl text-sm font-bold shadow-sm hover:bg-black" onClick={handleSaveExperience}>Save Experience</button>
                      <button type="button" className="bg-gray-200 text-gray-700 px-6 py-2.5 rounded-xl text-sm font-bold hover:bg-gray-300" onClick={() => setIsAddingExperience(false)}>Cancel</button>
                    </div>
                  </div>
                )}

                {experienceList.length === 0 ? (
                  <p className="text-gray-500 font-medium">No work experience added yet.</p>
                ) : (
                  <div className="space-y-4">
                    {experienceList.map((exp, index) => (
                      <div key={exp.id || index} className="p-5 border border-gray-200 rounded-2xl flex justify-between items-start bg-white hover:border-yellow-300 transition-colors shadow-sm">
                        <div>
                          <h4 className="font-extrabold text-gray-900 text-lg">{exp.jobTitle} <span className="text-gray-400 font-semibold">at</span> {exp.companyName}</h4>
                          <p className="text-xs text-blue-600 font-bold uppercase tracking-wider mt-1">{exp.employmentType} <span className="text-gray-400 px-1">•</span> {exp.startDate} to {exp.endDate}</p>
                          <p className="text-sm text-gray-600 font-medium mt-3 leading-relaxed">{exp.jobDescription}</p>
                        </div>
                        <button onClick={() => handleDeleteExperience(exp.id, index)} className="text-red-500 text-sm font-bold hover:underline shrink-0 ml-4">Delete</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'education' && (
              <div>
                <div className="flex justify-between items-center mb-6 pb-3 border-b border-gray-100">
                  <h2 className="text-xl font-extrabold text-gray-900">Education Details</h2>
                  <button type="button" className="bg-yellow-500 text-black px-4 py-2 rounded-xl text-sm font-bold shadow-sm hover:bg-yellow-600" onClick={() => setIsAddingEducation(true)}>+ Add Education</button>
                </div>

                {isAddingEducation && (
                  <div className="bg-gray-50 p-6 rounded-2xl border border-gray-200 mb-6 space-y-4">
                    <h3 className="font-extrabold text-gray-900">Add New Education</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <input type="text" placeholder="Qualification (e.g. B.Tech)" value={educationForm.qualification} onChange={(e) => setEducationForm({...educationForm, qualification: e.target.value})} className="border-2 border-gray-200 p-3 rounded-xl outline-none focus:border-yellow-500 font-semibold" />
                      <input type="text" placeholder="College / University" value={educationForm.collegeUniversity} onChange={(e) => setEducationForm({...educationForm, collegeUniversity: e.target.value})} className="border-2 border-gray-200 p-3 rounded-xl outline-none focus:border-yellow-500 font-semibold" />
                      <input type="text" placeholder="Specialization" value={educationForm.specialization} onChange={(e) => setEducationForm({...educationForm, specialization: e.target.value})} className="border-2 border-gray-200 p-3 rounded-xl outline-none focus:border-yellow-500 font-semibold" />
                      <input type="text" placeholder="Percentage / CGPA" value={educationForm.percentageCgpa} onChange={(e) => setEducationForm({...educationForm, percentageCgpa: e.target.value})} className="border-2 border-gray-200 p-3 rounded-xl outline-none focus:border-yellow-500 font-semibold" />
                      <input type="text" placeholder="Passing Year" value={educationForm.passingYear} onChange={(e) => setEducationForm({...educationForm, passingYear: e.target.value})} className="border-2 border-gray-200 p-3 rounded-xl outline-none focus:border-yellow-500 font-semibold" />
                    </div>
                    <div className="flex gap-3 pt-2">
                      <button type="button" className="bg-gray-900 text-yellow-500 px-6 py-2.5 rounded-xl text-sm font-bold shadow-sm hover:bg-black" onClick={handleSaveEducation}>Save Education</button>
                      <button type="button" className="bg-gray-200 text-gray-700 px-6 py-2.5 rounded-xl text-sm font-bold hover:bg-gray-300" onClick={() => setIsAddingEducation(false)}>Cancel</button>
                    </div>
                  </div>
                )}

                {educationList.length === 0 ? (
                  <p className="text-gray-500 font-medium">No education records added yet.</p>
                ) : (
                  <div className="space-y-4">
                    {educationList.map((edu, index) => (
                      <div key={edu.id || index} className="p-5 border border-gray-200 rounded-2xl flex justify-between items-start bg-white hover:border-yellow-300 transition-colors shadow-sm">
                        <div>
                          <h4 className="font-extrabold text-gray-900 text-lg">{edu.qualification} - {edu.specialization}</h4>
                          <p className="text-xs text-blue-600 font-bold uppercase tracking-wider mt-1">{edu.collegeUniversity} <span className="text-gray-400 px-1">•</span> Class of {edu.passingYear}</p>
                          <p className="text-sm text-gray-700 font-bold mt-2">Score/CGPA: <span className="text-green-600">{edu.percentageCgpa}</span></p>
                        </div>
                        <button onClick={() => handleDeleteEducation(edu.id, index)} className="text-red-500 text-sm font-bold hover:underline shrink-0 ml-4">Delete</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'resume' && (
              <div>
                <h2 className="text-xl font-extrabold text-gray-900 mb-6 pb-3 border-b border-gray-100">Resume Management</h2>
                <div className="flex flex-col sm:flex-row gap-4 items-center mb-8">
                  <label className="bg-yellow-500 text-black font-extrabold px-8 py-3.5 rounded-xl cursor-pointer hover:bg-yellow-600 shadow-sm transition-colors w-full sm:w-auto text-center">
                    Upload New Resume
                    <input type="file" accept=".pdf,.doc,.docx" onChange={handleResumeUpload} className="hidden" />
                  </label>
                  {resumeData && (
                    <div className="flex gap-3 w-full sm:w-auto">
                      <button onClick={handleDownloadResume} className="flex-1 sm:flex-none bg-gray-900 text-white font-bold px-6 py-3.5 rounded-xl hover:bg-black transition-colors shadow-sm">Download</button>
                      <button onClick={handleDeleteResume} className="flex-1 sm:flex-none bg-red-50 text-red-600 border border-red-200 font-bold px-6 py-3.5 rounded-xl hover:bg-red-100 transition-colors">Delete</button>
                    </div>
                  )}
                </div>

                {resumeData ? (
                  <div className="p-6 bg-gray-50 rounded-2xl border border-gray-200">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-white rounded-xl shadow-sm border border-gray-100 flex items-center justify-center">
                        <FileText className="text-blue-500" />
                      </div>
                      <div>
                        <p className="font-extrabold text-gray-900 text-base">{resumeData.name}</p>
                        <p className="text-gray-500 text-xs font-semibold mt-0.5">Updated: {resumeData.uploadDate}</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-10 border-2 border-dashed border-gray-200 rounded-2xl text-center">
                    <FileText className="mx-auto text-gray-300 w-12 h-12 mb-3" />
                    <p className="text-gray-500 font-bold text-lg mb-1">No resume uploaded</p>
                    <p className="text-gray-400 text-sm">Upload a PDF or Word document to get noticed by employers.</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'settings' && (
              <div>
                <h2 className="text-xl font-extrabold text-gray-900 mb-6 pb-3 border-b border-gray-100">Account Settings</h2>
                <div className="space-y-6 max-w-md">
                  <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
                    <h3 className="text-base font-extrabold text-gray-900 mb-4">Change Password</h3>
                    <div className="space-y-4">
                      
                      <div className="relative">
                        <input 
                          type={showCurrentPassword ? "text" : "password"} 
                          name="currentPassword" 
                          placeholder="Current Password" 
                          value={settingsData.currentPassword} 
                          onChange={handleSettingsInputChange} 
                          className="w-full border-2 border-gray-200 p-3 rounded-xl font-semibold outline-none focus:border-yellow-500 pr-12" 
                        />
                        <button type="button" onClick={() => setShowCurrentPassword(!showCurrentPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                          {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>

                      <div className="relative">
                        <input 
                          type={showNewPassword ? "text" : "password"} 
                          name="newPassword" 
                          placeholder="New Password" 
                          value={settingsData.newPassword} 
                          onChange={handleSettingsInputChange} 
                          className="w-full border-2 border-gray-200 p-3 rounded-xl font-semibold outline-none focus:border-yellow-500 pr-12" 
                        />
                        <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                          {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>

                      <div className="relative">
                        <input 
                          type={showConfirmPassword ? "text" : "password"} 
                          name="confirmPassword" 
                          placeholder="Confirm Password" 
                          value={settingsData.confirmPassword} 
                          onChange={handleSettingsInputChange} 
                          className="w-full border-2 border-gray-200 p-3 rounded-xl font-semibold outline-none focus:border-yellow-500 pr-12" 
                        />
                        <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                          {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>

                      <button onClick={handleSavePassword} className="w-full bg-yellow-500 text-black py-3 rounded-xl font-extrabold hover:bg-yellow-600 shadow-sm mt-2">Update Password</button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'faq' && (
              <div>
                <h2 className="text-xl font-extrabold text-gray-900 mb-6 pb-3 border-b border-gray-100">Frequently Asked Questions</h2>
                <div className="space-y-4">
                  {faqItems.map((item, index) => (
                    <div key={index} className="border border-gray-200 rounded-2xl overflow-hidden bg-white shadow-sm hover:border-yellow-300 transition-colors">
                      <button onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)} className="w-full flex justify-between items-center p-5 text-left font-extrabold text-gray-900">
                        <span>{item.question}</span>
                        <span className="text-yellow-500 text-xl leading-none">{openFaqIndex === index ? "-" : "+"}</span>
                      </button>
                      {openFaqIndex === index && (
                        <div className="p-5 pt-0 text-sm font-medium text-gray-600 leading-relaxed border-t border-gray-50 mt-2">
                          {item.answer}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>

        </div>

      </main>

      {showQRModal && createPortal(
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="relative w-full max-w-sm bg-white rounded-3xl shadow-2xl p-6 md:p-8 animate-in fade-in zoom-in-95">
            <button onClick={() => setShowQRModal(false)} className="absolute top-4 right-4 p-1.5 bg-gray-100 rounded-full text-gray-500 hover:bg-red-50 hover:text-red-500 transition-colors">
              <X className="w-5 h-5" />
            </button>

            <div className="text-center mb-6">
              <h3 className="text-xl font-extrabold text-gray-900 mb-1">Recharge Wallet</h3>
              <p className="text-sm font-semibold text-gray-500">Scan & Pay ₹100 for 1000 Coins</p>
            </div>

            <div className="bg-gray-50 p-4 rounded-2xl border-2 border-dashed border-gray-200 flex justify-center mb-6">
              <img 
                src={`https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(
                  `upi://pay?pa=${getUpiId()}&pn=Kadtech HR Solutions&am=100.00&cu=INR`
                )}`} 
                alt="Company UPI QR Code" 
                className="w-48 h-48 object-contain rounded-xl shadow-sm" 
              />
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-[11px] font-extrabold text-gray-500 mb-1.5 uppercase tracking-wider">Enter 12-Digit UTR No. *</label>
                <input 
                  type="text" 
                  value={utrNumber} 
                  onChange={(e) => setUtrNumber(e.target.value.replace(/\D/g, '').slice(0, 12))} 
                  placeholder="e.g. 123456789012" 
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm font-bold text-gray-900 focus:outline-none focus:border-yellow-500 bg-gray-50 focus:bg-white transition-colors text-center tracking-widest" 
                />
              </div>

              <button 
                onClick={async () => {
                  if (utrNumber.length < 12) return toast.error("Please enter a valid 12-digit UTR!");
                  setSubmittingUtr(true);
                  try {
                    const candidateData = JSON.parse(localStorage.getItem('candidate') || '{}');
                    await api.post('/submit-utr', { candidate_id: candidateData.id, utr_number: utrNumber });
                    toast.success("UTR Submitted! Admin will verify and add 1000 coins soon.");
                    setShowQRModal(false);
                    setUtrNumber('');
                  } catch (err) {
                    toast.error(err.response?.data?.message || "Failed to submit UTR");
                  } finally {
                    setSubmittingUtr(false);
                  }
                }}
                disabled={submittingUtr || utrNumber.length < 12} 
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-extrabold py-3.5 rounded-xl transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submittingUtr ? "Submitting..." : "Submit Payment Request"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showInactiveModal && createPortal(
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 animate-in fade-in duration-200">
              <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl p-8 text-center animate-in zoom-in-95 duration-200 border border-gray-100 relative">
                  
                  <div className="w-20 h-20 bg-red-50 border-8 border-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
                      <AlertCircle size={32} />
                  </div>
                  
                  <h3 className="font-black text-2xl text-gray-900 mb-3">Profile Inactive</h3>
                  
                  <p className="text-gray-600 text-[14px] font-semibold mb-8 leading-relaxed">
                      Your profile is currently inactive due to insufficient wallet balance. Please recharge your wallet to activate your profile and make it visible to recruiters.
                  </p>
                  
                  <div className="flex gap-3">
                      <button 
                          onClick={() => { 
                              setShowInactiveModal(false); 
                              setShowQRModal(true); 
                          }}
                          className="flex-1 py-3.5 rounded-xl bg-[#FED547] hover:bg-yellow-400 text-black font-extrabold shadow-md transition-colors"
                      >
                          Recharge Now
                      </button>
                  </div>
              </div>
          </div>,
          document.body
      )}

      {showDeleteAccountModal && createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl animate-in zoom-in-95">
            <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <User size={32} />
            </div>
            <h3 className="text-2xl font-black text-gray-900 text-center mb-2">Delete Account?</h3>
            <p className="text-sm font-medium text-gray-500 text-center mb-8">Are you sure you want to permanently delete your account? This action cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowDeleteAccountModal(false)} className="flex-1 bg-gray-100 text-gray-800 py-3 rounded-xl font-bold hover:bg-gray-200 transition-colors">Cancel</button>
              <button onClick={handleDeleteAccount} className="flex-1 bg-red-500 text-white py-3 rounded-xl font-bold hover:bg-red-600 shadow-md shadow-red-500/30 transition-colors">Delete</button>
            </div>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
}